import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { api } from '@/lib/api';
import { kontrolaHesla, HESLO_HINT } from '@/lib/passwordPolicy';
import type { UpdateHesloInput } from '@/types/api';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

export function ProfilPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Soucasne heslo bereme jen "pro potvrzeni" — backend ho neoveruje
  // (per spec), ale vyzadujeme ho v UI, at se nestane, ze nekdo nahodou
  // zmeni heslo otevrene relace.
  const [currentHeslo, setCurrentHeslo] = useState('');
  const [noveHeslo, setNoveHeslo] = useState('');
  const [confirmHeslo, setConfirmHeslo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Policy validace zrcadli backend (viz lib/passwordPolicy.ts) — duvody
  // ukazujeme inline a blokujeme submit, dokud heslo nevyhovuje.
  const hesloDuvody = noveHeslo.length > 0 ? kontrolaHesla(noveHeslo, user?.email) : [];
  const hesloMismatch = confirmHeslo.length > 0 && noveHeslo !== confirmHeslo;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (noveHeslo !== confirmHeslo) {
      toast({
        title: 'Hesla se neshodují',
        description: 'Zadejte stejné heslo do obou polí.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const body: UpdateHesloInput = { noveHeslo };
      await api<void>(`/uzivatele/${user.id}/heslo`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      toast({ title: 'Heslo bylo změněno' });
      setCurrentHeslo('');
      setNoveHeslo('');
      setConfirmHeslo('');
    } catch (err) {
      toast({
        title: 'Změna hesla selhala',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Profil</h2>
        <p className="text-sm text-muted-foreground">Informace o přihlášeném uživateli</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Účet</CardTitle>
          <CardDescription>E-mail a jméno spravuje administrátor.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* E-mail i jmeno jsou tu jen read-only — zmenu dela admin v sekci
              Uzivatele. Pouzivame disabled Input + hint, at je to vizualne jasne. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="profil-email">E-mail</Label>
              <Input
                id="profil-email"
                type="email"
                value={user?.email ?? ''}
                disabled
                readOnly
                title="E-mail mění administrátor"
              />
              <p className="text-xs text-muted-foreground">E-mail mění administrátor.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profil-jmeno">Jméno</Label>
              <Input
                id="profil-jmeno"
                value={user?.jmeno ?? ''}
                disabled
                readOnly
                title="Jméno mění administrátor"
              />
              <p className="text-xs text-muted-foreground">Jméno mění administrátor.</p>
            </div>
          </div>

          <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
            <div className="text-muted-foreground">Strana:</div>
            <div>
              <Badge variant={user?.strana === 'NASE_FIRMA' ? 'default' : 'secondary'}>
                {user?.strana ?? '—'}
              </Badge>
            </div>
            <div className="text-muted-foreground">Role:</div>
            <div className="flex flex-wrap gap-1">
              {user?.roles && user.roles.length > 0 ? (
                user.roles.map((r) => (
                  <Badge key={r} variant="outline" className="font-mono text-[10px]">
                    {r}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <div className="text-muted-foreground">Stav:</div>
            <div>
              {user?.aktivni ? (
                <Badge variant="success">Aktivní</Badge>
              ) : (
                <Badge variant="destructive">Neaktivní</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Změnit heslo</CardTitle>
          <CardDescription>
            Zadejte aktuální heslo pro potvrzení a nové heslo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current-heslo">Aktuální heslo</Label>
              <Input
                id="current-heslo"
                type="password"
                autoComplete="current-password"
                required
                value={currentHeslo}
                onChange={(e) => setCurrentHeslo(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profil-nove-heslo">Nové heslo</Label>
              <Input
                id="profil-nove-heslo"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={noveHeslo}
                onChange={(e) => setNoveHeslo(e.target.value)}
                disabled={submitting}
                aria-invalid={hesloDuvody.length > 0}
              />
              {hesloDuvody.length > 0 ? (
                <ul className="space-y-0.5 text-xs text-destructive">
                  {hesloDuvody.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">{HESLO_HINT}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profil-confirm-heslo">Potvrzení nového hesla</Label>
              <Input
                id="profil-confirm-heslo"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={confirmHeslo}
                onChange={(e) => setConfirmHeslo(e.target.value)}
                disabled={submitting}
                aria-invalid={hesloMismatch}
              />
              {hesloMismatch && (
                <p className="text-xs text-destructive">Hesla se neshodují.</p>
              )}
            </div>
            <Button
              type="submit"
              disabled={
                submitting ||
                !currentHeslo ||
                !noveHeslo ||
                !confirmHeslo ||
                hesloDuvody.length > 0 ||
                noveHeslo !== confirmHeslo
              }
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Změnit heslo'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
